import React, { useCallback, useEffect, useState } from 'react';
import { translate } from '../../Services/translation';
import EditorString from '../EditorString/EditorString';
import Checkbox from '../Inputs/Checkbox';
import Button from '../Button';
import { UserConsentModel } from '../../Types/UserInfoModel';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { saveUserConsents } from '../../Actions/UserInfo.action';
import { useTypedDispatch } from '../../Hooks/useTypedDispatch';
import { getColor } from '../../styling/mixins/colors';
import { fontFamilyBold, text } from '../../styling/mixins/typography';
import styled from 'styled-components';
import Icon from '../Icon';

const StyledGeneralMessage = styled.div`
  display: flex;
  align-items: center;
  color: ${getColor('alert')};
  margin-bottom: 1.6rem;
  ${text('medium')}

  span {
    margin-left: 0.4rem;
  }
`;

const StyledSuccessMessage = styled(StyledGeneralMessage)`
  color: ${getColor('success')};
  ${fontFamilyBold}
`;

const MyPagesMyConsents = ({ introText }) => {
    const dispatch = useTypedDispatch();

    const { current: userInfo, errors = {}, successMessage } = useTypedSelector(
        (state) => state.userInfo
    );

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [consentForm, setConsentForm] = useState<UserConsentModel>(userInfo?.userConsents || {
        customerClub: false,
        productReview: false
    });

    const submitUserConsents = (e: any) => {
        e.preventDefault();
        if (consentForm) {
            setIsLoading(true);
            dispatch(saveUserConsents(consentForm)).finally(() => setIsLoading(false));
        }
    };

    const hasErrors = useCallback(
        (key: string) => errors[key] && errors[key]?.length,
        [errors]
    );

    useEffect(() => {
        if (errors['general']?.length || !!successMessage) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [errors, successMessage]);

    return (
        <div className='flex flex-col'>
            <h1 className="title mb-4">{translate('mypage.consents.title')}</h1>

            {introText && introText.value && (
                <div className="description mb-4">
                    <EditorString value={introText.value} />
                </div>
            )}

            {hasErrors('general') ? (
                <StyledGeneralMessage>
                    <Icon name="warning" />
                    <span>{errors['general'] && errors['general'][0]}</span>
                </StyledGeneralMessage>
            ) : null}
            {successMessage ? (
                <StyledSuccessMessage>
                    <Icon name="check" />
                    <span>{successMessage}</span>
                </StyledSuccessMessage>
            ) : null}

            <form>
                <ContentSection>
                    <SubTitle value={translate('mypage.consents.productreview.title')} />
                    <Paragraph value={translate('mypage.consents.productreview.text')} />
                    <CheckboxWrapper>
                        <Checkbox
                            name="role-radio"
                            inputSize="large"
                            label={translate('mypage.consents.checkbox.yes')}
                            id="product-review-yes"
                            checked={consentForm.productReview}
                            onChange={() => {
                                setConsentForm(prev => ({
                                    ...prev,
                                    productReview: true
                                }))
                            }}
                            wrapperClassName="radio"
                        />

                        <Checkbox
                            name="role-radio"
                            inputSize="large"
                            label={translate('mypage.consents.checkbox.no')}
                            id="product-review-no"
                            checked={!consentForm.productReview}
                            onChange={() => {
                                setConsentForm(prev => ({
                                    ...prev,
                                    productReview: false
                                }))
                            }}
                            wrapperClassName="radio"
                        />
                    </CheckboxWrapper>
                </ContentSection>

                <ContentSection>
                    <SubTitle value={translate('mypage.consents.customerclub.title')} />
                    <Paragraph value={translate('mypage.consents.customerclub.text')} />

                    <CheckboxWrapper>
                        <Checkbox
                            name="role-radio"
                            inputSize="large"
                            label={translate('mypage.consents.checkbox.yes')}
                            id="customer-club-yes"
                            checked={consentForm.customerClub}
                            onChange={() => {
                                setConsentForm(prev => ({
                                    ...prev,
                                    customerClub: true
                                }))
                            }}
                            wrapperClassName="radio"
                        />

                        <Checkbox
                            name="role-radio"
                            inputSize="large"
                            label={translate('mypage.consents.checkbox.no')}
                            id="customer-club-no"
                            checked={!consentForm.customerClub}
                            onChange={() => {
                                setConsentForm(prev => ({
                                    ...prev,
                                    customerClub: false
                                }))
                            }}
                            wrapperClassName="radio"
                        />

                    </CheckboxWrapper>
                </ContentSection>

                <Button
                    btnSize="large"
                    btnType="primary"
                    className="w-full mt-4"
                    onClick={submitUserConsents}
                    disabled={isLoading}
                >
                    {translate('form.save-changes')}
                </Button>
            </form>
        </div>
    )
}

export default MyPagesMyConsents;


const SubTitle = ({ value }) => {
    return (
        <h5 className='mb-4'>{value}</h5>
    )
}

const Paragraph = ({ value }) => {
    return (
        <p className='mb-4'>{value}</p>
    )
}

const CheckboxWrapper = ({ children }) => {
    return (
        <div className='flex flex-col gap-2'>
            {children}
        </div>
    )
}

const ContentSection = ({ children }) => {
    return (
        <div className='my-4'>
            {children}
        </div>
    )
}